import { useEffect, useRef, useState } from "react";
import { usePixiBillingClient } from "../../billing/usePixiBillingClient";
import type { PixiAddress } from "../../hooks/usePixiWizardState";
import type { CheckoutPricing } from "./useCheckoutPricing";
import {
  buildEstimateRequest,
  estimateTotalCents,
  mapEstimateTaxes,
  type TaxLine,
} from "./mappers/estimateRequest";

export interface UseCheckoutEstimateArgs {
  pricing: CheckoutPricing;
  billingAddress: PixiAddress;
  shippingAddress: PixiAddress;
  vatNumber?: string;
  spaceId?: string;
  /**
   * When false, the estimate is never fetched and the hook stays idle. Lets a
   * caller that already has the tax-inclusive total (e.g. the Complete screen
   * reusing the value captured at purchase) skip a redundant request
   * (GRWBKK-1820). Defaults to true.
   */
  enabled?: boolean;
}

export interface CheckoutEstimate {
  /** Tax lines for the billing location; empty when no tax applies. */
  taxLines: TaxLine[];
  /**
   * The estimate's tax-inclusive total (licence + shipping + tax) in cents, or
   * `undefined` before an estimate has resolved — the caller falls back to the
   * client-computed total so the summary always shows an amount.
   */
  totalCents?: number;
  /** True while the estimate is in flight — show a non-blocking loading state. */
  loading: boolean;
  /** True when the estimate failed — block purchase (unknown tax amount). */
  error: boolean;
}

/**
 * Fetch the address-aware checkout estimate and expose its tax lines, so the
 * Order Summary can show the VAT/sales tax for the customer's billing location
 * and a tax-inclusive total (GRWBKK-1745).
 *
 * Refetches on mount and whenever the billing address, VAT number, or resolved
 * pricing changes. The request is only issued once a billing country is present
 * — before that (and in tests / server render, where there is no session token)
 * it stays idle with no tax and no error, so the summary is never blocked by an
 * estimate that can't yet be computed.
 */
export const useCheckoutEstimate = ({
  pricing,
  billingAddress,
  shippingAddress,
  vatNumber,
  spaceId,
  enabled = true,
}: UseCheckoutEstimateArgs): CheckoutEstimate => {
  const billing = usePixiBillingClient();
  const [taxLines, setTaxLines] = useState<TaxLine[]>([]);
  const [totalCents, setTotalCents] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const request = buildEstimateRequest({
    pricing,
    billingAddress,
    shippingAddress,
    vatNumber,
  });
  // A stable key over the request values drives the effect: it only refetches
  // when something that affects the tax actually changes, not on every render.
  const requestKey = JSON.stringify(request);
  // The request object itself is rebuilt each render; read it via a ref so the
  // effect depends only on the serialised key (not the fresh object identity).
  const requestRef = useRef(request);
  requestRef.current = request;

  const hasBillingCountry = Boolean(billingAddress.country);

  useEffect(() => {
    if (!enabled || !hasBillingCountry) {
      setTaxLines([]);
      setTotalCents(undefined);
      setError(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    billing
      .calculateEstimate(requestRef.current)
      .then((response) => {
        if (cancelled) return;
        setTaxLines(mapEstimateTaxes(response));
        setTotalCents(estimateTotalCents(response));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [requestKey, hasBillingCountry, spaceId, enabled, billing]);

  return { taxLines, totalCents, loading, error };
};
